import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Iter-297 — AllSale-branded Google sign-in return page.
 *
 * The backend redirects the browser here after a successful Google
 * consent + code exchange, embedding the JWT in the URL fragment:
 *
 *   /auth/google/done#gt=<jwt>&next=<path>
 *
 * We parse the hash, hand the token to AuthProvider (via a small
 * "apply this bearer + hydrate /auth/me" helper), then navigate to the
 * caller's original `next`. No `gt` in the hash → we bounce to /login.
 *
 * The hash is stripped via `history.replaceState` so a Back/refresh
 * cannot re-fire the same token or leak it into a bookmark.
 */
export default function GoogleDonePage() {
  const nav = useNavigate();
  const { setSessionFromToken } = useAuth();
  const consumed = useRef(false);

  useEffect(() => {
    if (consumed.current) return;
    consumed.current = true;

    const hash = (window.location.hash || "").replace(/^#/, "");
    const params = new URLSearchParams(hash);
    const gt = params.get("gt");
    const next = params.get("next") || "/";

    // Strip the fragment immediately regardless of outcome.
    try {
      window.history.replaceState(null, "", window.location.pathname);
    } catch { /* noop */ }

    if (!gt) {
      toast.error("Google sign-in failed — please try again.");
      nav("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        localStorage.setItem("allsale_token", gt);
        const { data: userObj } = await sharedApi.get("/auth/me");
        if (!userObj) throw new Error("No user returned from /auth/me");
        await setSessionFromToken(gt, userObj);
        toast.success("Signed in with Google.");
        nav(next && next.startsWith("/") ? next : "/", { replace: true });
      } catch (err) {
        localStorage.removeItem("allsale_token");
        const detail = err?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Google sign-in failed — please try again.");
        nav("/login", { replace: true });
      }
    })();
  }, [nav, setSessionFromToken]);

  return (
    <div className="max-w-md mx-auto px-4 py-24 text-center text-slate-500" data-testid="google-done-page">
      <Loader2 className="w-6 h-6 mx-auto animate-spin text-[color:var(--brand-blue)]" />
      <p className="mt-4 font-semibold">Finishing sign-in with Google…</p>
    </div>
  );
}
